/**
 * Typography System
 * Defines font families, weights, and size utilities
 */

export enum FontFamily {
  Primary = 'primary',
}

/**
 * Every family resolves through a CSS variable rather than naming a typeface.
 *
 * `--font` is declared in exactly one place: the `next/font` call in
 * `app/layout.tsx`, which sets it on `<html>`. So the ~230 `getFontFamily()`
 * and `FONT`/`FONT_FAMILY` call sites across the app, plus `globals.css`, all
 * follow whatever that declaration says — swapping the product typeface is one
 * edit, in one file.
 *
 * This used to restate the Avenir stack literally, which meant the token and
 * `globals.css` were two sources of truth for the same fact and could drift.
 */
export const FontFamilyValue: Record<FontFamily, string> = {
  [FontFamily.Primary]: 'var(--font)',
};

export enum FontWeight {
  Light = 300,
  Regular = 400,
  Medium = 500,
  SemiBold = 600,
  Bold = 700,
  Heavy = 800,
}

/**
 * Divisor `pxToRem` uses to turn a design-system pixel number into `rem`.
 *
 * This is NOT the document root font size. Nothing sets `font-size` on `html`
 * or `:root`, so the root stays at the browser default 16px, and dividing by 15
 * makes every `pxToRem` value render 16/15 (~6.67%) larger than the number
 * written in the code — font sizes, spacing, gaps and box sizes alike. That
 * uniform scale-up is the app's established rendered size, so this constant is
 * load-bearing for typography: changing it would resize all rendered text.
 *
 * Corner radii are deliberately kept out of that scale — they are absolute
 * geometry and are rendered in `px` by `getRadiusCssValue` in `theme/radius.ts`.
 */
export const BASE_FONT_SIZE = 15;

/**
 * Get font family CSS value
 */
export function getFontFamily(family: FontFamily = FontFamily.Primary): string {
  return FontFamilyValue[family];
}
